/**
 * Legacy dual-emit guard — pure, unit-tested (mirrors turn-state.ts).
 *
 * Decision #15 (docs/plans/2026-07-hubspot-native-handoff.md): the backend
 * dual-emits the legacy `handoff_trigger` event alongside the new
 * `handoff_started` event for the same handoff, for the duration of the
 * migration window — a rollback safety net for an old cached frontend build
 * that only knows how to open the (now-deleted) HubSpot widget on
 * `handoff_trigger`.
 *
 * This build's ENTIRE handoff UI is driven by `handoff_started` (see the
 * HANDOFF_STARTED case in useAssistantConnection.ts); the widget auto-open
 * flow `handoff_trigger` used to drive was deleted along with the widget
 * bridge module it lived in. So once `handoff_started` has been seen this WS
 * session, the paired legacy frame is pure noise and must be ignored outright.
 */
export function shouldIgnoreLegacyHandoffTrigger(
  sawHandoffStarted: boolean
): boolean {
  return sawHandoffStarted;
}

/**
 * Contact-form render-order gate — pure, unit-tested.
 *
 * `contact_info_requested` (Decision #17) is emitted at TOOL time, while the
 * Realtime model hasn't started writing its reply yet — so on the wire the
 * form event ALWAYS precedes Sara's "please fill in your details" prose.
 * Rendering the form immediately therefore puts it ABOVE the text (the same
 * structural misorder the tool cards had — see the deferred-cards block in
 * useAssistantConnection.ts). When a turn is in flight, the form must be
 * deferred and flushed with the tool cards the moment the prose lands
 * (text→form order).
 *
 * Two cases must NOT defer (insert immediately):
 * - Voice mode (`isCalling`): the event stops the call and switches to CHAT;
 *   there's no ordered text reply to wait for — same bypass the tool cards
 *   use. Callers pass the PRE-stopCall value, since the handler stops the
 *   call before inserting.
 * - No turn in flight (`!turnActive`): the backend RE-SENDS the event on
 *   reconnect while a handoff sits in `pending_contact` (see relay.py's
 *   resend_contact_info_request). That arrives outside any agent turn — no
 *   prose is coming, so a deferral would leave the form invisible until the
 *   last-resort fallback timer. `turnActive` mirrors the backend's
 *   turn_state contract (any phase other than idle).
 */
export function shouldDeferContactForm(
  isCalling: boolean,
  turnActive: boolean
): boolean {
  return !isCalling && turnActive;
}
